import * as React from 'react';
import { I18n, Translate } from 'react-redux-i18n';
import { Dispatch } from 'react-redux';

import { StoreState } from '../../../reducers';
import Popup, { PopupTheme } from '../../Popup';
import { PopupBodyWrapper, Button, Message } from './styles';
import ResourceSelector from '../../../containers/ResourceSelector';
import { ResourceSelectorTheme } from '../../ResourceSelector';
import SpreadsSelector from '../../../containers/Editor/CombinePanel/SpreadsSelector';
import { createCombo } from '../../../containers/Editor/actions';
import { removeFileFromImport } from '../../../containers/ImportManager/actions';
import { Api } from '../../../typings/api';
import DriveItemUtils from '../../../utils/driveItem';
import { NewStyleColor } from '../../../utils/styles-constants';
import Utils from '../../../utils';
import FromApp from '../../../containers/ResourceSelector/DrivePicker/FromApp';

interface CombinePanelProps {
	importManagerId: string;
	isDisplayed: boolean;
	isImporting: boolean;
	hasFromSelectionPicker: boolean;
	canSelectPublicationPages: boolean;
	onCancel: () => void;
	dispatch: Dispatch<StoreState>;
}

const desktopFileExtensions = [
	...DriveItemUtils.publicationExtensions,
	...DriveItemUtils.imageWithSvgExtensions,
	...DriveItemUtils.videoExtensions,
];

const CombinePanel = ({
	importManagerId,
	isDisplayed,
	isImporting,
	hasFromSelectionPicker,
	canSelectPublicationPages,
	onCancel,
	dispatch,
}: CombinePanelProps) => {
	const [resource, setResource] = React.useState<Api.DriveItem | null>(null);
	const [isOptionsDisplayed, setIsOptionsDisplayed] = React.useState(false);
	const [isSpreadsSelectorDisplayed, setIsSpreadsSelectorDisplayed] = React.useState(false);

	const handleCancelClick = () => {
		if (isImporting) {
			dispatch(removeFileFromImport(importManagerId));
		}

		onCancel();
	};

	const handleFullPublicationClick = () => {
		if (resource) {
			dispatch(createCombo(resource));
			setIsOptionsDisplayed(false);
			onCancel();
		}
	};

	const handlePagesSelectionClick = () => {
		setIsSpreadsSelectorDisplayed(true);
		setIsOptionsDisplayed(false);
		onCancel();
	};

	React.useEffect(() => {
		if (resource && !isImporting && !Utils.isPlaylistMode()) {
			if (
				resource.type === Api.DriveItemType.Publication &&
				resource.format && resource.format.includes(Api.PublicationFormat.EPUB) &&
				canSelectPublicationPages && resource.format !== Api.PublicationFormat.EPUB_PLAYLIST
			) {
				setIsOptionsDisplayed(true);
			} else {
				dispatch(createCombo(resource));
				onCancel();
			}
		} else if (Utils.isPlaylistMode() && resource && !isImporting) {
			setIsSpreadsSelectorDisplayed(true);
			setIsOptionsDisplayed(false);
			onCancel();
		}
	}, [resource, isImporting, canSelectPublicationPages, Utils.isPlaylistMode()]);

	return (
		<>
			<Popup
				isDisplayed={isDisplayed && !isOptionsDisplayed && !isSpreadsSelectorDisplayed && !Utils.isPlaylistMode()}
				label={I18n.t('messages.combine')}
				onCancel={handleCancelClick}
				actionButtonColor={NewStyleColor.Blue450}
				theme={PopupTheme.DARK}
			>
				<PopupBodyWrapper>
					<ResourceSelector
						id={importManagerId}
						fileExtensions={desktopFileExtensions}
						isFileExtensionsDisplayed={false}
						isValidResourceFromApp={DriveItemUtils.isValidResourceForPublication}
						isFromSelection={hasFromSelectionPicker}
						isPublicationDependency={true}
						actionButtonColor={NewStyleColor.Blue450}
						theme={ResourceSelectorTheme.DARK}
						onSelect={([item]) => setResource(item)}
					/>
				</PopupBodyWrapper>
			</Popup>
			{isDisplayed && !isSpreadsSelectorDisplayed && Utils.isPlaylistMode() &&
 				<FromApp
					isValidResource={DriveItemUtils.isValidResourceForPlaylist}
					onSelect={([item]) => setResource(item)}
					onClose={handleCancelClick}
					actionButtonColor={NewStyleColor.Blue450}
					isMultipleSelection={false}
				/>
			}
			<Popup
				isDisplayed={isOptionsDisplayed && !isSpreadsSelectorDisplayed && !Utils.isPlaylistMode()}
				label="&nbsp;"
				onCancel={() => setIsOptionsDisplayed(false)}
				actionButtonColor={NewStyleColor.Blue450}
				theme={PopupTheme.DARK}
			>
				<PopupBodyWrapper>
					<Button onClick={handleFullPublicationClick}>
						<Translate value="messages.combine" />
					</Button>
					<Button
						onClick={handlePagesSelectionClick}
					>
						<Translate value="messages.pages_selection" />
					</Button>
					{resource && !resource.published &&
						<Message>
							{Utils.getSvgIcon('iconset_uprez_info2')}
							<Translate value="messages.pages_selection_tooltip" />
						</Message>
					}
				</PopupBodyWrapper>
			</Popup>
			{isSpreadsSelectorDisplayed && resource &&
				<SpreadsSelector
					resourceId={resource.id}
					onClose={() => setIsSpreadsSelectorDisplayed(false)}
				/>
			}
		</>
	);
};

export default CombinePanel;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/CombinePanel/index.tsx